import React, { useEffect, useState } from "react";
import { taskApi } from "../../backend/client";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

const MeetingForm = ({ handleCloseForm, taskId, onSave }) => {
  const [formData, setFormData] = useState({
    date: "",
    time: "",
    link: "",
    attendees: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchMeetingDetails = async () => {
      if (!taskId) return;

      const { data } = await taskApi.get(taskId);

      if (data?.meeting_details) {
        try {
          const parsed = JSON.parse(data.meeting_details);
          setFormData({
            date: parsed.date || "",
            time: parsed.time || "",
            link: parsed.link || "",
            attendees: parsed.attendees || "",
          });
        } catch (error) {
          console.error("Invalid JSON in meeting_details:", error);
        }
      }
    };

    fetchMeetingDetails();
  }, [taskId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    if (!formData.date) {
      setErrorMessage("Date is required");
      return;
    }
    if (!formData.time) {
      setErrorMessage("Time is required");
      return;
    }

    try {
      const { data, error } = await taskApi.update(taskId, {
        meeting_details: JSON.stringify(formData),
      });

      if (error) {
        setErrorMessage("Failed to save meeting details");
        return;
      }

      setSuccessMessage("Meeting details saved successfully.");
      onSave?.(data);
    } catch (error) {
      console.error(error);
      setErrorMessage("An unexpected error occurred");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && handleCloseForm?.()}>
      <DialogContent>
        <form onSubmit={handleSave} className="space-y-5">
          <DialogHeader>
            <DialogTitle>Meeting Details</DialogTitle>
            <DialogDescription>
              Add or edit the meeting date, time, link, and attendees.
            </DialogDescription>
          </DialogHeader>

          {errorMessage && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="rounded-md border border-tone-success-border bg-tone-success-surface px-3 py-2 text-sm text-tone-success-foreground">
              {successMessage}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="meeting-date">Date</Label>
              <Input
                id="meeting-date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meeting-time">Time</Label>
              <Input
                id="meeting-time"
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                required
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="meeting-link">Link</Label>
              <Input
                id="meeting-link"
                name="link"
                value={formData.link}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="meeting-attendees">Attendees</Label>
              <Textarea
                id="meeting-attendees"
                name="attendees"
                value={formData.attendees}
                onChange={handleChange}
                placeholder="List attendees here"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCloseForm}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MeetingForm;
